<script lang="ts">
  // Service alerts view with native date picker and chip filters.
  // Systemwide service alerts page.
  import { InputBare } from '@byjackli-labs/ui/Input'
  import { Button } from '@byjackli-labs/ui/Button'
  import { EmptyState } from '@byjackli-labs/ui/EmptyState'
  import { Colors, getRouteColor } from '../ui/colors'
  import { chipChrome, ROW_RULE, Type } from '../ui/theme'
  import { useFavoritesStore } from '../store/favorites'
  import { useTransitStore, type ServiceAlert } from '../store/transit'
  import { useBackHandler } from '../hooks/useBackHandler.svelte'
  import { storeSelect } from '../lib/store.svelte'
  import RouteBullet from './Alerts/RouteBullet.svelte'
  import AlertCard from './Alerts/AlertCard.svelte'
  import {
    CHIP_DATE_FORMAT,
    LINE_TITLE,
    SUBWAY_GROUPS,
    dateInputToMs,
    routeKind,
    toDateInput,
    type Kind,
    type When,
  } from './Alerts/lines'
  import type { Snippet } from 'svelte'
  import {
    alertCountsByLine,
    alertKindCounts,
    alertsInGroup,
    alertsInScope,
    alertsOnLines,
    filterAlerts,
    maxAlertDate,
    sectionBusFavorites,
    subwayAlertCounts,
    subwayGroupTotals,
    suggestAlertRoutes,
  } from './Alerts/viewModel'

  interface Props {
    onSelectRoutes?: () => void
    /**
     * Publishes the bus line search up into the sheet's title row, where it sits beside ✕.
     * The snippet is rendered by Sheet but closes over this component's filter state, which
     * is the whole reason it travels as a snippet rather than being rebuilt up there.
     */
    onHeaderActions?: (actions: Snippet | null) => void
  }

  const { onSelectRoutes, onHeaderActions }: Props = $props()

  const FILTER_CHIP_FILL = 'flex: 1 1 0; min-width: 0; justify-content: center;'

  // Both halves of the merged Upcoming chip step down from the 12px floor: the label, its
  // count and a date have to share half a phone's width, and the alternative is letting this
  // one chip grow, which desyncs the four-chip grid FILTER_CHIP_FILL exists to hold.
  const MERGED_CHIP_TEXT = 'font-size: 11px; line-height: 1.2;'

  const alerts = storeSelect(useTransitStore, (s) => s.alerts)
  const upcomingAlerts = storeSelect(useTransitStore, (s) => s.upcomingAlerts)
  const upcomingStatus = storeSelect(useTransitStore, (s) => s.upcomingAlertsStatus)
  // Upcoming planned work isn't pushed over the socket; it loads when this sheet opens (the
  // sheet is mounted only while open), and again on a later open once it is 5 min old.
  $effect(() => { useTransitStore.getState().loadUpcomingAlerts() })
  const favoriteBusRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteBusRoutes)

  let kind = $state<Kind>('subway')
  let when = $state<When>('now')
  // YYYY-MM-DD date filter
  let dateFilter = $state<string | null>(null)
  let dateInputEl = $state<HTMLInputElement>()

  // Multi-select rather than one-at-a-time: a commute is two or three lines, and checking
  // them one after another is the slow path this filter exists to remove.
  let lines = $state<string[]>([])
  let query = $state('')
  let pickerOpen = $state(false)

  // Bus line search filter state.
  //
  // `searchShown` also covers a filter left standing without the flag: switching to Subway
  // drops `searchOpen` but keeps the lines, and coming back to Bus must show them.
  let searchOpen = $state(false)
  let searchBarEl = $state<HTMLDivElement>()
  const searchShown = $derived(searchOpen || lines.length > 0)


  const SEARCH_BAR =
    'flex: 1 1 auto; min-width: 128px; overflow-x: auto; scrollbar-width: none;'
    + ' padding: 3px 3px 3px 8px; border-radius: var(--bjl-radius-md, 10px);'
  /** Compact inside the bar, header-sized when it is the only thing showing. */
  const SEARCH_ICON_IN_BAR =
    'width: 26px; height: 26px; min-width: 26px; min-height: 26px; padding: 0;'
    + ' font-size: 13px; flex-shrink: 0;'
  const SEARCH_ICON_ALONE =
    'width: 34px; height: 34px; min-width: 34px; min-height: 34px; padding: 0;'
    + ' flex-shrink: 0;'

  // Selected subway group detail.
  let openGroup = $state<string | null>(null)
  const group = $derived(SUBWAY_GROUPS.find((g) => g.name === openGroup) ?? null)

  // MTA publishes planned work a month ahead, outnumbering live disruptions ~5:1. One list
  // buries the three delays happening now under a hundred weekend reroutes, so "when" is a
  // separate axis from "which mode".
  const live = $derived(alerts.value)
  // Minus anything already live: the REST list is up to 5 min old, and work that has since
  // begun arrives over the socket — listed under Now, not under both.
  const upcoming = $derived.by(() => {
    const liveIds = new Set(live.map((a) => a.id))
    return (upcomingAlerts.value ?? []).filter((a) => !liveIds.has(a.id))
  })

  // Bounded to what MTA has published (today through the last upcoming period) so riders
  // can't scroll into empty months.
  const dateMax = $derived(maxAlertDate(upcoming))

  // The date filter only applies to upcoming planned work; "Now" is always the full live set.
  const inScope = $derived(alertsInScope(live, upcoming, when, dateFilter, dateInputToMs))

  // Inside a group: every line in it, not only the flagged ones, so the drill-in is a
  // complete answer for the corridor.
  const inGroup = $derived(alertsInGroup(inScope, group?.lines))

  // Runs before the mode tabs, so their counts describe the list actually on screen: with
  // the F selected, "Bus 12" would otherwise promise twelve alerts the filter then hides.
  const onLines = $derived(alertsOnLines(inGroup, lines))

  const filtered = $derived(filterAlerts(onLines, kind))

  const counts = $derived(alertKindCounts(onLines))

  // Aggregates subway alerts per line.
  const subwayPerLine = $derived(subwayAlertCounts(inScope))

  // Aggregates alert counts per subway group.
  const groupTotals = $derived(subwayGroupTotals(inScope))

  // Group alerts with favorited bus lines first.
  const sections = $derived(sectionBusFavorites(filtered, kind, favoriteBusRoutes.value))

  // Shown next to each bullet in the picker. Counted over the whole scope, so a line's
  // number doesn't drop to zero the moment another line is selected.
  const alertsPerLine = $derived(alertCountsByLine(inScope))

  // Only lines with an alert in scope: the point is to skip the scan, and a picker listing
  // all ~250 bus routes rebuilds it. The mode tabs narrow it too, so "Bus" never suggests a
  // train.
  const suggestions = $derived(suggestAlertRoutes(alertsPerLine, lines, kind, query))

  function toggleLine(routeId: string) {
    lines = lines.includes(routeId) ? lines.filter((r) => r !== routeId) : [...lines, routeId]
    // Clear on pick, so the next line can be typed without deleting the old text.
    query = ''
  }

  function clearLines() {
    lines = []
    query = ''
  }

  function toggleSearch() {
    if (searchShown) {
      dismissSearch()
      return
    }
    searchOpen = true
    pickerOpen = true
    // InputBare spreads its props onto the real `<input>`, so it can't take a `bind:this`;
    // the bar's wrapper is the ref we have. Focus after paint: the field only mounts on
    // this state change, so there is nothing to focus yet.
    requestAnimationFrame(() => searchBarEl?.querySelector('input')?.focus())
  }

  /**
   * Putting the bar away also empties it. That is what makes it collapsible at all: a filter
   * left running behind an icon would shorten the list with nothing on screen saying why, so
   * the closed state and the unfiltered state are deliberately the same state.
   */
  function dismissSearch() {
    clearLines()
    searchOpen = false
    pickerOpen = false
  }

  // Bus only, and never inside a group; same rule the filter itself has always followed.
  const showLineSearch = $derived(!group && kind === 'bus')

  // Hand the control to the sheet's title row, and take it back when there is nothing to
  // show or this page unmounts (the sheet drops it on close). `lineSearch` is a snippet
  // declared in the markup below; it is a stable per-instance value, so re-running this
  // effect can't loop.
  $effect(() => {
    onHeaderActions?.(showLineSearch ? lineSearch : null)
    return () => onHeaderActions?.(null)
  })

  function handleSelect(alert: ServiceAlert) {
    const routes = [...new Set(alert.routeIds)].filter(Boolean)
    if (routes.length === 0) return
    const transit = useTransitStore.getState()
    transit.setSelectedRoutes(routes)
    // A selected line renders only under its own type gate, so switch the gate to match
    // rather than selecting lines the map silently hides.
    transit.setTransitFilter(routes.map(routeKind))
    onSelectRoutes?.()
  }

  // Desktop focuses a date field on click rather than opening the calendar, so ask for the
  // dialog explicitly. Mobile opens its own on tap, where showPicker() can throw, hence the
  // try, and hence the input being a real tap target.
  function openDatePicker() {
    if (!dateInputEl || typeof dateInputEl.showPicker !== 'function') return
    try {
      dateInputEl.showPicker()
    } catch {
      /* mobile opens natively on tap */
    }
  }

  // Five chips share one row, ~57px each on a 360px phone, so NJ Transit goes by its own
  // abbreviation on the chip and by its name to a screen reader.
  const tabs: { key: Kind; label: string; spoken?: string }[] = [
    { key: 'subway', label: 'Subway' },
    { key: 'bus', label: 'Bus' },
    { key: 'ferry', label: 'Ferry' },
    { key: 'rail', label: 'Rail' },
    { key: 'njbus', label: 'NJT', spoken: 'NJ Transit buses' },
  ]

  // Clears any line filter left from the index: it was typed against a different list, and
  // carrying it in would hide alerts the row's own count just promised.
  function openGroupRow(name: string) {
    openGroup = name
    clearLines()
    pickerOpen = false
  }

  // Android Back button handler
  useBackHandler(() => group !== null, () => { openGroup = null })

  /** The subway index screen: trunk groups only, no cards. Bus and any drill-in show cards. */
  const isIndex = $derived(kind === 'subway' && !group)

  const noun = $derived(when === 'now' ? 'active alerts' : 'planned changes')

  // Narrowest cause first. A line filter matching nothing must say so: "No active alerts"
  // reads as "the whole system is fine", a much bigger claim than "nothing on the F".
  const empty = $derived.by(() => {
    // Before anything about filters: an Upcoming list that hasn't arrived is not empty.
    if (when === 'upcoming' && upcomingStatus.value !== 'loaded') {
      return upcomingStatus.value === 'error'
        ? { title: "Couldn't load planned changes", subtitle: 'Check your connection, then reopen Alerts.' }
        : { title: 'Loading planned changes…', subtitle: undefined, icon: false }
    }
    if (lines.length > 0) {
      return {
        title: `No ${noun} on ${lines.join(', ')}`,
        subtitle: 'Try another line, or clear the filter to see every alert.',
      }
    }
    if (group) {
      return {
        title: `No ${noun} on these lines`,
        subtitle:
          when === 'now'
            ? 'Check Upcoming for work MTA has scheduled here.'
            : 'Nothing scheduled on this group.',
      }
    }
    if (inScope.length > 0) {
      return {
        // Subway and Bus word this exactly as they always have. Ferry is its own operator, so
        // "the subway has alerts" says nothing to someone waiting for a boat: title only.
        title: `No ${kind} ${noun}`,
        subtitle: kind === 'subway' ? `Buses have ${noun}.` : kind === 'bus' ? `The subway has ${noun}.` : undefined,
        icon: kind !== 'ferry',
      }
    }
    if (when === 'now') {
      return {
        title: 'No active alerts',
        subtitle: 'Every line is running without a reported disruption.',
      }
    }
    if (dateFilter) {
      return {
        title: `No planned changes on ${CHIP_DATE_FORMAT.format(dateInputToMs(dateFilter))}`,
        subtitle: 'Try another date, or clear the filter to see the full schedule.',
      }
    }
    return {
      title: 'No planned changes',
      subtitle: 'Nothing scheduled on the lines MTA has published.',
    }
  })

  // Tint behind a picked bullet: primary at ~12%, so the chip reads selected without a
  // second border.
  const TINT = `${Colors.primary}1f`
</script>

<!-- Subway group row snippet -->
{#snippet subwayGroupRow(name: string, groupLines: string[], total: number)}
  {@const hasAlerts = total > 0}
  <!-- A ruled list row, not a card: the index is one continuous column of lines
       divided by hairlines, so eleven trunk groups read as one scannable table rather than
       eleven separate objects. The leading 6px bar is the trunk's own color: it is what
       lets a rider find "the orange lines" before reading anything.

       A group with nothing wrong drops to 45% and says "Good service" where the count would
       be. Quiet, not hidden: a line with nothing wrong is still an answer the rider came for. -->
  <button
    onclick={hasAlerts ? () => openGroupRow(name) : undefined}
    disabled={!hasAlerts}
    aria-label={hasAlerts ? `Show ${name} alerts` : `${name}: no alerts`}
    style="
      display: flex; align-items: center; gap: 12px; width: 100%;
      padding: 13px 0; text-align: left;
      background: none; border: none; border-bottom: {ROW_RULE};
      cursor: {hasAlerts ? 'pointer' : 'default'};
      opacity: {hasAlerts ? 1 : 0.45};
    "
  >
    <span
      aria-hidden="true"
      style="
        flex-shrink: 0; width: 6px; height: 34px; border-radius: 3px;
        background-color: {getRouteColor(groupLines[0])};
      "
    ></span>

    <span style="display: flex; gap: 4px; flex-shrink: 0; flex-wrap: wrap;">
      {#each groupLines as r (r)}
        <span title={LINE_TITLE[r]} aria-label={LINE_TITLE[r]} style="position: relative; display: inline-flex;">
          <RouteBullet routeId={r} />
          {#if (subwayPerLine.get(r) ?? 0) > 0}
            <span
              aria-hidden="true"
              style="
                position: absolute; top: -4px; right: -4px;
                display: inline-flex; align-items: center; justify-content: center;
                width: 9px; height: 9px; font-size: 9px; line-height: 9px;
                filter: drop-shadow(0 0 2px rgba(0,0,0,0.8));
              "
            >⚠️</span>
          {/if}
        </span>
      {/each}
    </span>

    <!-- The trunk's name earns its place here (it did not on a card): with the bar and the
         bullets already carrying the color, this is the row's only piece of prose. -->
    <span
      style="
        flex: 1; min-width: 0; color: {Colors.textSecondary}; {Type.body};
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      "
    >{name}</span>

    {#if hasAlerts}
      <span
        style="
          flex-shrink: 0; {Type.body}; font-weight: 700; color: {Colors.text};
          background-color: {Colors.surfaceElevated};
          border-radius: 999px; padding: 4px 10px;
        "
      >{total}</span>
      <span aria-hidden="true" style="flex-shrink: 0; color: rgba(255,255,255,0.35); font-size: 15px;">›</span>
    {:else}
      <span style="flex-shrink: 0; {Type.caption}; color: {Colors.textSecondary};">Good service</span>
    {/if}
  </button>
{/snippet}

<!-- Header bus line search filter snippet -->
{#snippet lineSearch()}
  <!-- One box in both states: open it is the bar (chrome, and it takes the leftover width),
       closed it is nothing but the icon's own wrapper. Opening on click as well as focus:
       a tap that lands on the bar's padding rather than the input itself should still reveal
       the lines, and it keeps the picker reachable in webviews stingy about firing focus. -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    bind:this={searchBarEl}
    onclick={() => { if (searchShown) pickerOpen = true }}
    style="
      display: flex; align-items: center; gap: 4px; flex-wrap: nowrap; min-width: 0;
      {searchShown
        ? `${SEARCH_BAR} background-color: ${Colors.surfaceElevated};
           border: 1px solid ${lines.length > 0 ? Colors.primary : Colors.border};`
        : 'flex: 0 0 auto;'}
    "
  >
    {#if searchShown}
      <!-- nowrap, not the wrap a full-width bar could afford: this one shares a row with the
           sheet's title, and a second line of bullets would push the header taller and shove
           the whole panel's contents down. Three-plus lines scroll sideways instead. -->
      {#each lines as r (r)}
        <span
          style="
            display: inline-flex; align-items: center; gap: 3px; flex-shrink: 0;
            padding-right: 3px; border-radius: 12px; background-color: {TINT};
          "
        >
          <RouteBullet routeId={r} />
          <Button
            variant="text"
            size="sm"
            iconOnly
            onclick={() => toggleLine(r)}
            aria-label="Remove {r} from the line filter"
            style="
              width: 18px; height: 18px; min-width: 18px; min-height: 18px;
              padding: 0; font-size: 10px; line-height: 1;
            "
          >✕</Button>
        </span>
      {/each}

      <InputBare
        bind:value={query}
        class="alerts-line-input"
        oninput={() => { pickerOpen = true }}
        onfocus={() => { pickerOpen = true }}
        onblur={() => { pickerOpen = false }}
        onkeydown={(e: KeyboardEvent) => {
          // Enter takes the top suggestion: filtering to a known line is type-and-go.
          if (e.key === 'Enter' && suggestions.length > 0) toggleLine(suggestions[0])
          // Backspace on an empty field peels off the last line, as any chip input does.
          else if (e.key === 'Backspace' && !query && lines.length > 0)
            toggleLine(lines[lines.length - 1])
          else if (e.key === 'Escape') (e.currentTarget as HTMLInputElement | null)?.blur()
        }}
        placeholder={lines.length > 0 ? 'Add' : 'Line: F, M15…'}
        aria-label="Filter alerts by line"
        style="
          flex: 1; min-width: 56px; padding: 2px 0; font-size: 13px;
          background: none; border: none; box-shadow: none;
        "
      />

      <!-- Empties the field without putting it away, so the next line can be typed. Only
           when there is something to empty; the icon beside it is always the way out. -->
      {#if lines.length > 0 || query}
        <Button
          variant="text"
          size="sm"
          iconOnly
          onclick={(e: MouseEvent) => { e.stopPropagation(); clearLines() }}
          aria-label="Clear line filter"
          style="
            flex-shrink: 0;
            width: 20px; height: 20px; min-width: 20px; min-height: 20px;
            padding: 0; font-size: 11px; line-height: 1;
          "
        >✕</Button>
      {/if}
    {/if}

    <!-- The icon is the collapsed filter and, once open, the way back out, which is why it
         rides inside the bar's right edge instead of outside it. stopPropagation because the
         wrapper it sits in is also the bar's open-the-picker target, and closing would
         otherwise reopen the picker on the way up. -->
    <Button
      variant="text"
      size="sm"
      iconOnly
      onclick={(e: MouseEvent) => { e.stopPropagation(); toggleSearch() }}
      aria-expanded={searchShown}
      aria-label={searchShown ? 'Close the line filter' : 'Filter alerts by line'}
      style={searchShown ? SEARCH_ICON_IN_BAR : SEARCH_ICON_ALONE}
    >🔍</Button>
  </div>
{/snippet}

<div style="display: flex; flex-direction: column; flex: 1; min-height: 0;">
  <!-- Filter chips (Now / Upcoming) -->
  <div style="display: flex; align-items: center; gap: 8px; padding: 14px 20px 14px; flex-shrink: 0;">
    {#each [{ key: 'now' as const, label: 'Now', total: live.length }, { key: 'upcoming' as const, label: 'Upcoming', total: upcoming.length }] as { key, label, total } (key)}
      {@const active = when === key}
      {#if key === 'upcoming' && active}
        <!-- Upcoming date filter chip -->
        <div
          style="
            {chipChrome('on')} {FILTER_CHIP_FILL}
            gap: 6px; padding: 4px 5px 4px 10px; min-height: 30px; box-sizing: border-box;
          "
        >
          <button
            onclick={() => { when = key }}
            aria-pressed="true"
            style="
              {MERGED_CHIP_TEXT} min-width: 0; overflow: hidden; text-overflow: ellipsis;
              background: none; border: none; padding: 0; margin: 0;
              font-family: inherit; font-weight: inherit; color: inherit; white-space: nowrap;
            "
          >
            <!-- The horizon-wide count is dropped once a date is picked: the list below is
                 then one day long, and "· 134" beside it describes a set that isn't shown. -->
            {label}{total > 0 && !dateFilter ? ` · ${total}` : ''}
          </button>

          <!-- The date's own tap target. Tinted rather than bordered so it reads as a well
               sunk into the white plate, which is what makes it look pressable at 11px. -->
          <span
            style="
              position: relative; display: inline-flex; align-items: center; gap: 3px;
              flex-shrink: 0; border-radius: 6px; padding: 3px 6px;
              background: rgba(0,0,0,0.12); {MERGED_CHIP_TEXT}
            "
          >
            <span style="min-width: 0; overflow: hidden; text-overflow: ellipsis;">
              {dateFilter ? CHIP_DATE_FORMAT.format(dateInputToMs(dateFilter)) : 'Date'}
            </span>
            <!-- The caret stays whether or not a date is set: it is the only thing saying a
                 picker opens here, and with a date showing it's the only thing saying the
                 date can still be changed. -->
            <span aria-hidden="true" style="opacity: 0.6;">▾</span>
            <!-- The real input is stretched transparently over this well and takes the tap
                 itself. Driving it from a button via showPicker() instead left mobile with no
                 way in: showPicker() is unavailable or refused there, and focus()/click() on
                 an off-screen input summons no native picker. -->
            <input
              bind:this={dateInputEl}
              type="date"
              value={dateFilter ?? ''}
              min={toDateInput(Date.now())}
              max={dateMax}
              aria-label="Filter planned changes by date"
              onclick={openDatePicker}
              onchange={(e) => {
                const value = e.currentTarget.value || null
                dateFilter = value
                // A date only means anything against the upcoming set, so follow the user there.
                if (value) when = 'upcoming'
              }}
              style="
                position: absolute; inset: 0; z-index: 1;
                width: 100%; height: 100%;
                opacity: 0; cursor: pointer;
                border: none; padding: 0; margin: 0; background: none;
                -webkit-appearance: none; appearance: none;
              "
            />
          </span>

          <!-- Outside the well, so clearing never lands on the input overlay and opens the
               picker on the way out. -->
          {#if dateFilter}
            <Button
              variant="text"
              size="sm"
              iconOnly
              onclick={() => { dateFilter = null }}
              aria-label="Clear date filter"
              style="
                flex-shrink: 0;
                width: 16px; height: 16px; min-width: 16px; min-height: 16px;
                padding: 0; border-radius: 8px; font-size: 10px; line-height: 1;
                background: rgba(0,0,0,0.12); color: inherit;
              "
            >✕</Button>
          {/if}
        </div>
      {:else}
        <button
          onclick={() => { when = key }}
          aria-pressed={active}
          style="{chipChrome(active ? 'on' : 'outlined')} cursor: pointer; {FILTER_CHIP_FILL}"
        >
          {label}{total > 0 ? ` · ${total}` : ''}
        </button>
      {/if}
    {/each}
  </div>

  <!-- Inside a group the mode chips and the line filter are gone: the header below already
       says what this list is, and a Bus chip on a screen full of trains only invites a tap
       that would have to throw the group away. Now/Upcoming stays, since planned work on the
       corridor is the other half of the same question. -->
  {#if group}
    <div style="display: flex; align-items: center; gap: 10px; padding: 0 20px 12px; flex-shrink: 0;">
      <Button
        variant="secondary"
        size="sm"
        onclick={() => { openGroup = null }}
        aria-label="Back to all subway lines"
        style="flex-shrink: 0;"
      >‹ All lines</Button>

      <span style="display: flex; gap: 4px; flex: 1; min-width: 0; flex-wrap: wrap;">
        {#each group.lines as r (r)}
          <span title={LINE_TITLE[r]} aria-label={LINE_TITLE[r]}>
            <RouteBullet routeId={r} />
          </span>
        {/each}
      </span>

      <span style="flex-shrink: 0; color: {Colors.textMuted}; {Type.caption};">
        {filtered.length} {filtered.length === 1 ? 'alert' : 'alerts'}
      </span>
    </div>
  {:else}
    <div style="display: flex; align-items: center; gap: 8px; padding: 0 20px 12px; flex-shrink: 0;">
      {#each tabs as { key, label, spoken } (key)}
        {@const active = kind === key}
        <!-- Leaving Bus puts the search away with it, filter and all. The header control is
             gone on Subway but `lines` is not scoped to a tab, so a bus route left selected
             would filter the subway index down to nothing with no control on screen saying
             why, the same invisible-filter problem collapsing the bar has to avoid. -->
        <button
          onclick={() => { kind = key; if (key !== 'bus') dismissSearch() }}
          aria-pressed={active}
          aria-label={spoken ? `${spoken}${counts[key] > 0 ? `, ${counts[key]}` : ''}` : undefined}
          style="{chipChrome(active ? 'on' : 'off')} cursor: pointer; {FILTER_CHIP_FILL}"
        >
          {label}{counts[key] > 0 ? ` · ${counts[key]}` : ''}
        </button>
      {/each}
    </div>
  {/if}

  <!-- The suggestions the header's search offers, dropped into the page directly beneath the
       bar that produced them. They belong to the page, not the title row: a list that can run
       to a dozen bus routes cannot live in a 30px-tall row of chrome. -->
  {#if showLineSearch && searchShown && pickerOpen && suggestions.length > 0}
    <!-- mousedown is what steals focus from the input; suppressing its default keeps the
         field focused so the picker doesn't blur itself shut before the tap registers. -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      onmousedown={(e) => e.preventDefault()}
      style="
        display: flex; flex-wrap: wrap; gap: 6px; flex-shrink: 0;
        max-height: 108px; overflow-y: auto; padding: 0 20px 12px;
      "
    >
      {#each suggestions as r (r)}
        <Button
          variant="secondary"
          size="sm"
          onclick={() => toggleLine(r)}
          aria-label="Show only {r} alerts"
          style="display: inline-flex; align-items: center; gap: 5px;"
        >
          <RouteBullet routeId={r} />
          {alertsPerLine.get(r)}
        </Button>
      {/each}
    </div>
  {/if}

  <div
    style="
      flex: 1; min-height: 0; overflow-y: auto;
      padding: 0 20px 20px;
      display: flex; flex-direction: column; gap: 10px;
    "
  >
    <!-- The subway tab is the index and nothing else. Every alert it summarises is one tap
         away inside its group, and repeating them underneath as a flat list would put the
         same alert on screen twice (once counted, once in full) which is the pile the
         grouping exists to replace. -->
    {#if isIndex}
      <div style="display: flex; flex-direction: column;">
        {#each SUBWAY_GROUPS as { name, lines: groupLines } (name)}
          {@render subwayGroupRow(name, groupLines, groupTotals.get(name) ?? 0)}
        {/each}
      </div>
    {:else if filtered.length === 0}
      {#if 'icon' in empty && !empty.icon}
        <EmptyState title={empty.title} description={empty.subtitle} />
      {:else}
        <EmptyState title={empty.title} description={empty.subtitle}>
          {#snippet icon()}
            <span style="font-size: 26px;">
              {lines.length > 0 ? '🔍' : when === 'now' ? '✓' : '🗓'}
            </span>
          {/snippet}
        </EmptyState>
      {/if}
    {:else}
      {#each sections as { label, alerts: section } (label ?? 'all')}
        <div style="display: flex; flex-direction: column; gap: 10px;">
          {#if label}
            <div
              style="color: {Colors.textMuted}; {Type.eyebrow};"
            >{label}</div>
          {/if}
          {#each section as alert (alert.id)}
            <AlertCard {alert} onSelect={() => handleSelect(alert)} />
          {/each}
        </div>
      {/each}
    {/if}
  </div>
</div>
